import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import {
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

const formatNumber = (value) => {
  const num = parseFloat(value) || 0;
  return num.toFixed(2);
};

const DiffCell = ({ value }) => {
  const num = parseFloat(value) || 0;
  const isPositive = num > 0;
  const isNegative = num < 0;

  return (
    <span className={`font-medium ${isPositive ? 'text-green-600' : ''} ${isNegative ? 'text-red-600' : ''}`}>
      {isPositive ? '+' : ''}{formatNumber(value)}
    </span>
  );
};

// Helper to extract value from nested before/after structure or flat structure
const getValue = (item, field, type) => {
  // Check if using nested before/after structure (from API)
  if (item[field] && typeof item[field] === 'object' && (type in item[field])) {
    return item[field][type];
  }
  // Fallback to flat structure (beforeField, afterField, diffField)
  const prefix = type === 'before' ? 'before' : type === 'after' ? 'after' : 'diff';
  const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
  return item[`${prefix}${capitalizedField}`];
};

export const CorrectionViewPositionsSection = ({ correctionItems }) => {
  const { t } = useTranslation();

  if (!correctionItems || correctionItems.length === 0) {
    return null;
  }

  const columns = [
    {
      accessorKey: "name",
      header: t("invoices:positions_name"),
      cell: ({ row }) => {
        const name = getValue(row.original, 'name', 'after') || row.original.name;
        return <div className="min-w-[150px]">{name}</div>;
      },
    },
    {
      accessorKey: "beforeAmount",
      header: `${t("invoices:before_value")} ${t("invoices:positions_amount")}`,
      cell: ({ row }) => <div>{getValue(row.original, 'amount', 'before') ?? row.original.beforeAmount}</div>,
    },
    {
      accessorKey: "afterAmount",
      header: `${t("invoices:after_value")} ${t("invoices:positions_amount")}`,
      cell: ({ row }) => <div>{getValue(row.original, 'amount', 'after') ?? row.original.afterAmount}</div>,
    },
    {
      accessorKey: "diffAmount",
      header: `${t("invoices:diff_value")}`,
      cell: ({ row }) => <DiffCell value={getValue(row.original, 'amount', 'diff') ?? row.original.diffAmount} />,
    },
    {
      accessorKey: "beforePriceNetto",
      header: `${t("invoices:before_value")} ${t("invoices:price_netto")}`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'priceNetto', 'before') ?? row.original.beforePriceNetto)}</div>,
    },
    {
      accessorKey: "afterPriceNetto",
      header: `${t("invoices:after_value")} ${t("invoices:price_netto")}`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'priceNetto', 'after') ?? row.original.afterPriceNetto)}</div>,
    },
    {
      accessorKey: "diffPriceNetto",
      header: `${t("invoices:diff_value")}`,
      cell: ({ row }) => <DiffCell value={row.original.diffNetto} />,
    },
    {
      accessorKey: "beforeVat",
      header: `${t("invoices:before_value")} VAT`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'vat', 'before') ?? row.original.beforeVat)}</div>,
    },
    {
      accessorKey: "afterVat",
      header: `${t("invoices:after_value")} VAT`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'vat', 'after') ?? row.original.afterVat)}</div>,
    },
    {
      accessorKey: "diffVat",
      header: `${t("invoices:diff_value")}`,
      cell: ({ row }) => <DiffCell value={getValue(row.original, 'vat', 'diff') ?? row.original.diffVat} />,
    },
    {
      accessorKey: "beforePriceBrutto",
      header: `${t("invoices:before_value")} ${t("invoices:price_brutto")}`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'priceBrutto', 'before') ?? row.original.beforePriceBrutto)}</div>,
    },
    {
      accessorKey: "afterPriceBrutto",
      header: `${t("invoices:after_value")} ${t("invoices:price_brutto")}`,
      cell: ({ row }) => <div>{formatNumber(getValue(row.original, 'priceBrutto', 'after') ?? row.original.afterPriceBrutto)}</div>,
    },
    {
      accessorKey: "diffPriceBrutto",
      header: `${t("invoices:diff_value")}`,
      cell: ({ row }) => <DiffCell value={row.original.diffBrutto} />,
    },
  ];

  const table = useReactTable({
    data: correctionItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full pb-6">
      <div className="text-xl font-medium pb-6">
        {t("invoices:correction_positions")}
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={uuidv4()}>
                {headerGroup.headers.map(header => (
                  <TableHead className="pt-3 pb-3 bg-white text-xs" key={uuidv4()}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  className="bg-white"
                  key={row.id}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell className="relative text-sm pt-3 pb-3" key={uuidv4()}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t('no_results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
